import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'revoked') return 'default';
  return 'default';
}

export default function TenantApiKeysListPage() {
  const navigate = useNavigate();
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    try {
      const res = await tenantApiFetch(`/api/tenant/api-keys?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load API keys');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
  };

  const rows = data?.apiKeys || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">API keys</Typography>
        <TenantCan permission="tenant.api_keys.manage">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tenant/api-keys/new')}>
            Create key
          </Button>
        </TenantCan>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Secrets are shown only once when you create or rotate a key. Store them in a vault; they cannot be retrieved later.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            label="Search"
            size="small"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="created_at">Created</MenuItem>
              <MenuItem value="updated_at">Updated</MenuItem>
              <MenuItem value="last_used_at">Last used</MenuItem>
              <MenuItem value="expires_at">Expires</MenuItem>
              <MenuItem value="key_name">Name</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Newest first</MenuItem>
              <MenuItem value="asc">Oldest first</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Prefix / masked</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scopes</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right"> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((k) => (
                  <TableRow key={k.id} hover>
                    <TableCell>{k.key_name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {k.masked_key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={k.status} color={statusColor(k.status)} />
                    </TableCell>
                    <TableCell>
                      {(k.scopes || []).slice(0, 3).join(', ')}
                      {(k.scopes || []).length > 3 ? '…' : ''}
                    </TableCell>
                    <TableCell>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>{k.created_at ? new Date(k.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => navigate(`/tenant/api-keys/${k.id}`)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">No API keys yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
